import { useStore } from '../stores/store';
import * as api from './api';

/**
 * Login user
 */
export async function loginUser(email, password) {
  const result = await api.login(email, password);
  
  if (result.success && result.data.token) {
    useStore.getState().login(result.data.user, result.data.token);
    return { success: true };
  }
  
  return { success: false, error: result.error || 'Login failed' };
}

/**
 * Logout user (notifies server for audit, then clears local state)
 */
export async function logoutUser() {
  try {
    await api.logout();
  } catch (_) { /* ignore */ }
  useStore.getState().logout();
}

/**
 * Get effective user for permission/role checks (view-as context if set, else real user)
 */
export function getEffectiveUser() {
  const { user, viewAsUserContext } = useStore.getState();
  if (viewAsUserContext) return viewAsUserContext;
  return user;
}

/** Permission keys (must match backend PERMISSION_KEYS) */
const PERMISSION_KEYS = [
  'dashboard', 'tickets', 'alerts', 'returns', 'operations', 'knowledge_base',
  'ai_agent', 'ai_playground', 'ai_feedback', 'ai_costs', 'analytics', 'settings', 'handbook', 'users'
];

const DEFAULT_PERMISSIONS_BY_ROLE = {
  warehouse: ['operations', 'returns', 'handbook'],
  agent: ['dashboard', 'tickets', 'alerts', 'returns', 'operations', 'knowledge_base', 'handbook'],
  team: ['dashboard', 'tickets', 'alerts', 'returns', 'operations', 'knowledge_base', 'handbook'],
  manager: ['dashboard', 'tickets', 'alerts', 'returns', 'operations', 'knowledge_base', 'analytics', 'ai_feedback', 'settings', 'handbook'],
  admin: PERMISSION_KEYS,
};

/**
 * Check if user has a given permission (page/feature access).
 * Uses effective user (view-as or real); admin has all.
 * Falls back to role defaults when permissions array is empty.
 */
export function hasPermission(permissionKey) {
  const user = getEffectiveUser();
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = Array.isArray(user.permissions) && user.permissions.length > 0
    ? user.permissions
    : (DEFAULT_PERMISSIONS_BY_ROLE[user.role] || []);
  return perms.includes(permissionKey);
}

/**
 * Path to permission key map for route protection. Keyed by first path segment
 * (getPermissionForPath). NIGHT OPS: the new Night Ops base segments are the
 * live destinations; the OLD segments are kept because their 301 redirect
 * routes still render through ProtectedRoute before navigation completes
 * (spec 03 §5). No new PERMISSION_KEYS are invented — every path maps to an
 * existing key.
 */
const PATH_PERMISSION = {
  '/': 'dashboard',
  // Night Ops destinations
  '/guide': 'dashboard',
  '/queue': 'operations',
  '/conversations': 'tickets',
  '/refunds': 'returns',
  '/payment-holds': 'operations',
  '/disputes': 'alerts',
  '/order-ops': 'operations',
  '/shipping-holds': 'operations',
  '/returns': 'returns',
  '/performance': 'analytics',
  '/reviews': 'ai_feedback',
  '/playground': 'ai_playground',
  '/brain': 'knowledge_base',
  '/controls': 'ai_agent',
  '/spend': 'ai_costs',
  '/exports': 'analytics',
  '/team': 'users',
  '/ask': 'analytics',
  '/settings': 'settings',
  // Legacy segments (kept for the 301 redirect routes)
  '/tickets': 'tickets',
  '/actions': 'tickets',
  '/alerts': 'alerts',
  '/held-orders': 'operations',
  '/cc-protection-holds': 'operations',
  '/order-changes': 'operations',
  '/operations': 'operations',
  '/tracking-review': 'operations',
  '/inbox': 'operations',
  '/pending-refunds': 'returns',
  '/knowledge': 'knowledge_base',
  '/ai-agent': 'ai_agent',
  '/testing-lab': 'ai_playground',
  '/analytics': 'analytics',
  '/ai-feedback': 'ai_feedback',
  '/ai-costs': 'ai_costs',
  '/emma': 'analytics',
  '/emma-performance': 'analytics',
  '/haris-audits': 'ai_feedback',
  '/retention': 'analytics',
  '/handbook': 'handbook',
  '/users': 'users',
  '/admin': 'users',
};

export function getPermissionForPath(pathname = '') {
  const normalized = typeof pathname === 'string' ? pathname : '';
  if (normalized === '/' || normalized === '') return 'dashboard';
  const [firstSegment] = normalized.split('/').filter(Boolean);
  const base = firstSegment ? `/${firstSegment}` : '/';
  return PATH_PERMISSION[base] ?? null;
}
